import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getUserHistory,
  deleteHistory,
  deleteMultipleHistory,
  updateHistory,
  GenerationHistory,
  isVideoHistoryCategory,
} from '../../services/history';
import { getVideoModelConfig, getVideoTaskStatus, getVideoUrlFromTaskResult } from '../../services/video';
import Navbar from '../home/components/Navbar';
import Sidebar from '../home/components/Sidebar';

type AssetTab = 'all' | 'video' | 'prompt' | 'source' | 'history';

const ASSET_TABS: { id: AssetTab; name: string; icon: string; desc: string }[] = [
  { id: 'all', name: '全部素材', icon: 'ri-apps-line', desc: '全部创作产出' },
  { id: 'video', name: '生成视频', icon: 'ri-video-line', desc: '成片视频归档' },
  { id: 'prompt', name: '提示词', icon: 'ri-text-snippet', desc: '提示词检索复用' },
  { id: 'source', name: '原始素材', icon: 'ri-image-add-line', desc: '上传参考图/原片' },
  { id: 'history', name: '历史任务', icon: 'ri-history-line', desc: '任务流水与状态' },
];

function parseTags(item: GenerationHistory): string[] {
  return Array.isArray(item.tags) ? item.tags : [];
}

function parseInputImages(item: GenerationHistory): string[] {
  return Array.isArray(item.input_images) ? item.input_images.filter(Boolean) : [];
}

export default function MaterialsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<GenerationHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [assetTab, setAssetTab] = useState<AssetTab>('all');
  const [searchInput, setSearchInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [activeTag, setActiveTag] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [playbackUrl, setPlaybackUrl] = useState<{ url: string; isVideo: boolean } | null>(null);
  const [tagEditItem, setTagEditItem] = useState<GenerationHistory | null>(null);
  const [tagDraft, setTagDraft] = useState('');
  const [savingTags, setSavingTags] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const itemsRef = useRef<GenerationHistory[]>([]);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    if (user) loadItems();
  }, [user, keyword, activeTag]);

  const loadItems = async () => {
    if (!user) return;
    setLoading(true);
    setLoadError(null);
    const result = await getUserHistory(user.id, {
      limit: 200,
      q: keyword || undefined,
      tag: activeTag || undefined,
    });
    if (result.error) {
      setLoadError(result.error);
      setItems([]);
    } else {
      setItems(result.history);
    }
    setLoading(false);
  };

  const refreshPendingVideoTasks = async (list: GenerationHistory[]) => {
    const pending = list.filter(
      (item) =>
        item.task_id &&
        (item.status === 'pending' || item.status === 'processing') &&
        isVideoHistoryCategory(item.category)
    );
    if (pending.length === 0) return;
    const config = await getVideoModelConfig();
    if (!config?.api_key) return;
    for (const item of pending.slice(0, 10)) {
      try {
        const taskData = await getVideoTaskStatus(config, item.task_id!);
        if (!taskData) continue;
        if (taskData.status === 'succeeded') {
          const videoUrl = getVideoUrlFromTaskResult(taskData);
          if (videoUrl) {
            await updateHistory(item.id, { status: 'succeeded', output_url: videoUrl });
            setItems((prev) =>
              prev.map((h) => (h.id === item.id ? { ...h, status: 'succeeded', output_url: videoUrl } : h))
            );
          }
        } else if (taskData.status === 'failed' || taskData.status === 'expired') {
          await updateHistory(item.id, { status: taskData.status });
          setItems((prev) => prev.map((h) => (h.id === item.id ? { ...h, status: taskData.status! } : h)));
        }
      } catch {
        // ignore
      }
    }
  };

  useEffect(() => {
    if (!user) return;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let alive = true;
    const poll = async () => {
      if (!alive) return;
      const list = itemsRef.current;
      const hasPending = list.some(
        (item) =>
          item.task_id &&
          (item.status === 'pending' || item.status === 'processing') &&
          isVideoHistoryCategory(item.category)
      );
      if (!hasPending) return;
      await refreshPendingVideoTasks(list);
    };
    const t = setTimeout(() => {
      poll();
      intervalId = setInterval(poll, 5000);
    }, 2000);
    return () => {
      alive = false;
      clearTimeout(t);
      if (intervalId) clearInterval(intervalId);
    };
  }, [user, items.length]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    items.forEach((item) => parseTags(item).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (assetTab === 'all') return true;
      if (assetTab === 'video') {
        return isVideoHistoryCategory(item.category) && !!item.output_url;
      }
      if (assetTab === 'prompt') {
        return !!(item.prompt && item.prompt.trim());
      }
      if (assetTab === 'source') {
        return parseInputImages(item).length > 0;
      }
      if (assetTab === 'history') return true;
      return true;
    });
  }, [items, assetTab]);

  const handleSearch = () => {
    setKeyword(searchInput.trim());
  };

  const handleDelete = async (id: string) => {
    if (!confirm('确定删除该素材？')) return;
    const result = await deleteHistory(id);
    if (result.success) {
      setItems((prev) => prev.filter((h) => h.id !== id));
      setSelectedItems((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleBatchDelete = async () => {
    if (selectedItems.size === 0) return;
    if (!confirm(`确定删除选中的 ${selectedItems.size} 条素材？`)) return;
    const ids = Array.from(selectedItems);
    const result = await deleteMultipleHistory(ids);
    if (result.success) {
      setItems((prev) => prev.filter((h) => !selectedItems.has(h.id)));
      setSelectedItems(new Set());
    } else {
      alert(result.error || '批量删除失败');
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openTagEditor = (item: GenerationHistory) => {
    setTagEditItem(item);
    setTagDraft(parseTags(item).join(', '));
  };

  const saveTags = async () => {
    if (!tagEditItem) return;
    setSavingTags(true);
    const tags = tagDraft
      .split(/[,，]/)
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 20);
    const result = await updateHistory(tagEditItem.id, { tags });
    if (result.success) {
      setItems((prev) => prev.map((h) => (h.id === tagEditItem.id ? { ...h, tags } : h)));
      setTagEditItem(null);
    } else {
      alert(result.error || '保存标签失败');
    }
    setSavingTags(false);
  };

  const copyPrompt = async (item: GenerationHistory) => {
    if (!item.prompt) return;
    try {
      await navigator.clipboard.writeText(item.prompt);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      alert('复制失败，请手动选择文本');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const categoryLabel = (category: string) => {
    const map: Record<string, string> = {
      'ai-image': 'AI生图',
      'style-transfer': '风格转换',
      'sora2-video': '视频生成',
      'veo31-video': '首尾帧视频',
      video: '一键出大片',
    };
    return map[category] || category;
  };

  if (!user) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar activeMenu="素材空间" onMenuChange={() => {}} />
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <Navbar />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <i className="ri-folder-user-line text-6xl text-gray-300 mb-4"></i>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">请先登录</h2>
              <p className="text-gray-600 mb-6">登录后即可管理教学素材归档</p>
              <a
                href="/login"
                className="inline-block px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
              >
                去登录
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar activeMenu="素材空间" onMenuChange={() => {}} />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <Navbar />
        <main className="flex-1 overflow-y-auto overflow-x-hidden min-w-0">
          <div className="max-w-7xl mx-auto main-content-padding">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">素材空间</h1>
                <p className="text-gray-600 text-sm max-w-2xl">
                  对生成视频、提示词与上传原始素材进行分类存储与标签检索，方便教学成果归档与回溯。历史任务已集成于此。
                </p>
              </div>
              <div className="flex items-center gap-3">
                {selectedItems.size > 0 && (
                  <button
                    type="button"
                    onClick={handleBatchDelete}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm cursor-pointer"
                  >
                    <i className="ri-delete-bin-line mr-1"></i>
                    删除选中 ({selectedItems.size})
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => window.REACT_APP_NAVIGATE?.('/history')}
                  className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 text-sm cursor-pointer"
                >
                  <i className="ri-history-line mr-1"></i>
                  经典历史视图
                </button>
              </div>
            </div>

            {/* 分类 */}
            <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
              {ASSET_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setAssetTab(tab.id)}
                  title={tab.desc}
                  className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap cursor-pointer transition-all ${
                    assetTab === tab.id
                      ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white'
                      : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <i className={`${tab.icon} mr-2`}></i>
                  {tab.name}
                </button>
              ))}
            </div>

            {/* 搜索 + 标签 */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 space-y-3">
              <div className="flex flex-wrap gap-2">
                <div className="flex-1 min-w-[220px] flex gap-2">
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="搜索提示词关键词…"
                    className="flex-1 px-4 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-teal-500"
                  />
                  <button
                    type="button"
                    onClick={handleSearch}
                    className="px-4 py-2 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600 cursor-pointer"
                  >
                    搜索
                  </button>
                  {(keyword || activeTag) && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchInput('');
                        setKeyword('');
                        setActiveTag('');
                      }}
                      className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm cursor-pointer"
                    >
                      清空
                    </button>
                  )}
                </div>
              </div>
              {allTags.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-gray-500">标签筛选：</span>
                  {allTags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setActiveTag(activeTag === tag ? '' : tag)}
                      className={`px-2.5 py-1 rounded-full text-xs cursor-pointer ${
                        activeTag === tag
                          ? 'bg-teal-500 text-white'
                          : 'bg-teal-50 text-teal-700 hover:bg-teal-100'
                      }`}
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : loadError ? (
              <div className="text-center py-20">
                <p className="text-red-500 mb-4">{loadError}</p>
                <button type="button" onClick={loadItems} className="px-4 py-2 bg-teal-500 text-white rounded-lg cursor-pointer">
                  重试
                </button>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-folder-open-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">暂无素材</h3>
                <p className="text-gray-600">生成视频或上传参考图后，将自动归档到素材空间</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {filteredItems.map((item) => {
                  const tags = parseTags(item);
                  const inputs = parseInputImages(item);
                  const isVideo = isVideoHistoryCategory(item.category);
                  return (
                    <div
                      key={item.id}
                      className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col"
                    >
                      {/* 预览固定 1:1（300×300），object-cover 裁切，避免成片比例导致卡片高度不一 */}
                      <div className="relative mx-auto w-full max-w-[300px] aspect-square bg-gray-100 overflow-hidden">
                        <input
                          type="checkbox"
                          checked={selectedItems.has(item.id)}
                          onChange={() => toggleSelect(item.id)}
                          className="absolute top-3 left-3 z-10 w-4 h-4 cursor-pointer"
                        />
                        {assetTab === 'source' && inputs[0] ? (
                          <img src={inputs[0]} alt="原始素材" className="absolute inset-0 w-full h-full object-cover" />
                        ) : item.output_url ? (
                          isVideo ? (
                            <video
                              src={item.output_url}
                              className="absolute inset-0 w-full h-full object-cover"
                              muted
                              playsInline
                              preload="metadata"
                            />
                          ) : (
                            <img src={item.output_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                          )
                        ) : inputs[0] ? (
                          <img src={inputs[0]} alt="原始素材" className="absolute inset-0 w-full h-full object-cover" />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                            <i className="ri-file-unknow-line text-4xl"></i>
                          </div>
                        )}
                        <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/60 text-white text-xs">
                          {categoryLabel(item.category)}
                        </span>
                      </div>

                      <div className="p-4 flex-1 flex flex-col gap-3">
                        {(assetTab === 'prompt' || assetTab === 'all' || assetTab === 'history') && item.prompt && (
                          <div>
                            <p className="text-sm text-gray-800 line-clamp-3 whitespace-pre-wrap">{item.prompt}</p>
                            <button
                              type="button"
                              onClick={() => copyPrompt(item)}
                              className="mt-2 text-xs text-teal-600 hover:text-teal-700 cursor-pointer"
                            >
                              <i className="ri-file-copy-line mr-1"></i>
                              {copiedId === item.id ? '已复制' : '复制提示词'}
                            </button>
                          </div>
                        )}

                        {inputs.length > 0 && assetTab !== 'prompt' && (
                          <div className="flex gap-2 overflow-x-auto">
                            {inputs.slice(0, 4).map((url, idx) => (
                              <img
                                key={idx}
                                src={url}
                                alt={`原素材${idx + 1}`}
                                className="w-12 h-12 rounded object-cover border border-gray-200 shrink-0"
                              />
                            ))}
                          </div>
                        )}

                        <div className="flex flex-wrap gap-1.5">
                          {tags.map((tag) => (
                            <span key={tag} className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 text-xs">
                              #{tag}
                            </span>
                          ))}
                          <button
                            type="button"
                            onClick={() => openTagEditor(item)}
                            className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs hover:bg-gray-200 cursor-pointer"
                          >
                            <i className="ri-price-tag-3-line mr-0.5"></i>
                            标签
                          </button>
                        </div>

                        <div className="mt-auto flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-100">
                          <span>{formatDate(item.created_at)}</span>
                          <span
                            className={
                              item.status === 'succeeded'
                                ? 'text-emerald-600'
                                : item.status === 'failed'
                                  ? 'text-red-500'
                                  : 'text-amber-600'
                            }
                          >
                            {item.status === 'succeeded'
                              ? '已完成'
                              : item.status === 'failed'
                                ? '失败'
                                : item.status === 'pending' || item.status === 'processing'
                                  ? '生成中'
                                  : item.status}
                          </span>
                        </div>

                        <div className="flex gap-2">
                          {item.output_url && (
                            <button
                              type="button"
                              onClick={() =>
                                setPlaybackUrl({ url: item.output_url!, isVideo: isVideo })
                              }
                              className="flex-1 px-3 py-2 rounded-lg bg-teal-50 text-teal-700 text-sm hover:bg-teal-100 cursor-pointer"
                            >
                              {isVideo ? '播放' : '查看'}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleDelete(item.id)}
                            className="px-3 py-2 rounded-lg bg-red-50 text-red-600 text-sm hover:bg-red-100 cursor-pointer"
                          >
                            删除
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>

      {playbackUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setPlaybackUrl(null)}
        >
          <div
            className="bg-black rounded-xl overflow-hidden max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-end p-2">
              <button
                type="button"
                onClick={() => setPlaybackUrl(null)}
                className="w-8 h-8 text-white/80 hover:text-white cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {playbackUrl.isVideo ? (
              <video src={playbackUrl.url} controls autoPlay className="w-full max-h-[80vh]" />
            ) : (
              <img src={playbackUrl.url} alt="" className="w-full max-h-[80vh] object-contain" />
            )}
          </div>
        </div>
      )}

      {tagEditItem && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setTagEditItem(null)}
        >
          <div
            className="bg-white rounded-xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-2">编辑标签</h3>
            <p className="text-xs text-gray-500 mb-3">多个标签用逗号分隔，例如：课堂作业, 示范成片</p>
            <input
              type="text"
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 mb-4"
              placeholder="输入标签"
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setTagEditItem(null)}
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm cursor-pointer"
              >
                取消
              </button>
              <button
                type="button"
                onClick={saveTags}
                disabled={savingTags}
                className="px-4 py-2 rounded-lg bg-teal-500 text-white text-sm hover:bg-teal-600 disabled:opacity-50 cursor-pointer"
              >
                {savingTags ? '保存中…' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
